import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2,
  LogOut,
  Mail,
  Phone,
  MapPin,
  Building2,
  Trash2,
  Inbox,
  FileText,
  TrendingUp,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/lib/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  new: { label: "New", classes: "bg-[#FF5A00]/15 text-[#FF5A00] border-[#FF5A00]/40" },
  contacted: { label: "Contacted", classes: "bg-sky-500/15 text-sky-400 border-sky-500/40" },
  quoted: { label: "Quoted", classes: "bg-amber-500/15 text-amber-400 border-amber-500/40" },
  won: { label: "Won", classes: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" },
  lost: { label: "Lost", classes: "bg-zinc-500/15 text-zinc-400 border-zinc-500/40" },
};
const STATUS_ORDER = ["new", "contacted", "quoted", "won", "lost"];

export default function Admin() {
  const { user, token, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [busy, setBusy] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    async function load() {
      setBusy(true);
      try {
        const [s, q, c] = await Promise.all([
          axios.get(`${API}/admin/stats`, authHeaders),
          axios.get(`${API}/admin/quotes`, authHeaders),
          axios.get(`${API}/admin/contacts`, authHeaders),
        ]);
        if (cancelled) return;
        setStats(s.data);
        setQuotes(q.data);
        setContacts(c.data);
      } catch (e) {
        if (e?.response?.status === 401) logout();
        else toast.error("Could not load admin data.");
      } finally {
        if (!cancelled) setBusy(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [token, authHeaders, logout]);

  const deleteQuote = async (id) => {
    if (!window.confirm("Delete this quote? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/quotes/${id}`, authHeaders);
      setQuotes((q) => q.filter((x) => x.id !== id));
      toast.success("Quote deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this message? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, authHeaders);
      setContacts((c) => c.filter((x) => x.id !== id));
      toast.success("Message deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/quotes/${id}/status`,
        { status },
        authHeaders
      );
      setQuotes((qs) => qs.map((q) => (q.id === id ? data : q)));
      setStats((s) =>
        s
          ? {
              ...s,
              quotes_by_status: {
                ...(s.quotes_by_status || {}),
              },
            }
          : s
      );
      // refresh stats
      try {
        const s = await axios.get(`${API}/admin/stats`, authHeaders);
        setStats(s.data);
      } catch {
        // non-critical
      }
      toast.success(`Marked as ${STATUS_META[status]?.label || status}`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  const filteredQuotes = quotes
    .filter(filterFn(search))
    .filter((q) => statusFilter === "all" || (q.status || "new") === statusFilter);
  const filteredContacts = contacts.filter(filterFn(search));

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-zinc-950 grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#FF5A00]" />
      </div>
    );
  }

  return (
    <div data-testid="page-admin" className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Header */}
      <header className="border-b border-zinc-800 sticky top-0 bg-zinc-950/95 backdrop-blur-md z-40">
        <div className="container-max h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo variant="stacked" showSubtitle={false} />
            <span className="hidden md:inline-block px-3 py-1 border border-[#FF5A00]/40 text-[#FF5A00] font-heading text-[10px] tracking-[0.3em] uppercase">
              Admin Console
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-xs font-heading tracking-[0.2em] uppercase text-zinc-500">
              {user.email}
            </span>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="btn-secondary py-2 px-4 text-xs"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container-max py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard
            icon={FileText}
            label="Total quotes"
            value={stats?.quotes_total ?? "—"}
            testid="stat-quotes-total"
          />
          <StatCard
            icon={Inbox}
            label="Total messages"
            value={stats?.contacts_total ?? "—"}
            testid="stat-contacts-total"
          />
          <StatCard
            icon={TrendingUp}
            label="Quotes · last 7d"
            value={stats?.quotes_last_7d ?? "—"}
            testid="stat-quotes-week"
          />
          <StatCard
            icon={TrendingUp}
            label="Messages · last 7d"
            value={stats?.contacts_last_7d ?? "—"}
            testid="stat-contacts-week"
          />
        </div>

        {/* Search */}
        <div className="mb-6">
          <input
            data-testid="admin-search"
            type="text"
            placeholder="Search by name, email, phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-none h-11 px-4 text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-[#FF5A00]"
          />
        </div>

        <Tabs defaultValue="quotes">
          <TabsList className="bg-transparent p-0 h-auto gap-2 border-b border-zinc-800 w-full justify-start rounded-none">
            <TabsTrigger
              value="quotes"
              data-testid="tab-quotes"
              className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-[#FF5A00] data-[state=active]:border-b-2 data-[state=active]:border-[#FF5A00] text-zinc-400 hover:text-zinc-200 font-heading uppercase tracking-[0.2em] text-xs px-5 py-4 border-b-2 border-transparent"
            >
              Quote Requests ({filteredQuotes.length})
            </TabsTrigger>
            <TabsTrigger
              value="contacts"
              data-testid="tab-contacts"
              className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-[#FF5A00] data-[state=active]:border-b-2 data-[state=active]:border-[#FF5A00] text-zinc-400 hover:text-zinc-200 font-heading uppercase tracking-[0.2em] text-xs px-5 py-4 border-b-2 border-transparent"
            >
              Messages ({filteredContacts.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="quotes" className="mt-8">
            {/* Status filter chips */}
            <div className="flex flex-wrap gap-2 mb-6" data-testid="status-filters">
              {[{ key: "all", label: "All" }, ...STATUS_ORDER.map((s) => ({ key: s, label: STATUS_META[s].label }))].map(
                (chip) => {
                  const active = statusFilter === chip.key;
                  const count =
                    chip.key === "all"
                      ? quotes.length
                      : stats?.quotes_by_status?.[chip.key] ?? 0;
                  return (
                    <button
                      key={chip.key}
                      data-testid={`status-chip-${chip.key}`}
                      onClick={() => setStatusFilter(chip.key)}
                      className={`px-4 py-2 font-heading text-[10px] tracking-[0.25em] uppercase border transition-colors ${
                        active
                          ? "bg-[#FF5A00] border-[#FF5A00] text-zinc-950"
                          : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-[#FF5A00]"
                      }`}
                    >
                      {chip.label} <span className="ml-1.5 opacity-60">{count}</span>
                    </button>
                  );
                }
              )}
            </div>

            {busy ? (
              <Loading />
            ) : filteredQuotes.length === 0 ? (
              <Empty label="No quote requests match this filter." />
            ) : (
              <div className="space-y-4">
                {filteredQuotes.map((q) => (
                  <QuoteCard
                    key={q.id}
                    q={q}
                    onDelete={() => deleteQuote(q.id)}
                    onStatusChange={(s) => updateStatus(q.id, s)}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="contacts" className="mt-8">
            {busy ? (
              <Loading />
            ) : filteredContacts.length === 0 ? (
              <Empty label="No messages yet." />
            ) : (
              <div className="space-y-4">
                {filteredContacts.map((c) => (
                  <ContactCard key={c.id} c={c} onDelete={() => deleteContact(c.id)} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function filterFn(q) {
  const term = q.trim().toLowerCase();
  return (x) => {
    if (!term) return true;
    return [x.name, x.email, x.phone, x.company, x.message]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(term));
  };
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-zinc-900 border border-zinc-800 p-5">
      <div className="flex items-center gap-2 text-zinc-500">
        <Icon className="w-4 h-4 text-[#FF5A00]" />
        <span className="font-heading text-[10px] tracking-[0.3em] uppercase">
          {label}
        </span>
      </div>
      <div className="font-display text-4xl tracking-wider text-zinc-50 mt-3">
        {value}
      </div>
    </div>
  );
}

function QuoteCard({ q, onDelete, onStatusChange }) {
  const currentStatus = q.status || "new";
  const meta = STATUS_META[currentStatus] || STATUS_META.new;
  return (
    <article
      data-testid={`admin-quote-${q.id}`}
      className="bg-zinc-900 border border-zinc-800 p-6"
    >
      <header className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-2xl tracking-wider uppercase text-zinc-50">
            {q.name}
          </h3>
          <div className="font-heading text-[10px] tracking-[0.3em] uppercase text-[#FF5A00] mt-1">
            {q.service_type} {q.project_size ? `· ${q.project_size}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={`status-dropdown-${q.id}`}
                className={`inline-flex items-center gap-2 px-3 py-1.5 border text-xs font-heading uppercase tracking-[0.2em] ${meta.classes} hover:opacity-80 transition-opacity`}
              >
                {meta.label}
                <ChevronDown className="w-3 h-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-zinc-900 border border-zinc-800 rounded-none text-zinc-100 min-w-[180px]"
            >
              {STATUS_ORDER.map((s) => (
                <DropdownMenuItem
                  key={s}
                  data-testid={`status-option-${q.id}-${s}`}
                  onClick={() => onStatusChange(s)}
                  className={`font-heading text-xs tracking-[0.2em] uppercase cursor-pointer focus:bg-zinc-800 ${
                    s === currentStatus ? "text-[#FF5A00]" : "text-zinc-200"
                  }`}
                >
                  {STATUS_META[s].label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <span className="text-xs font-heading tracking-wider uppercase text-zinc-500">
            {new Date(q.created_at).toLocaleString()}
          </span>
          <button
            data-testid={`delete-quote-${q.id}`}
            onClick={onDelete}
            aria-label="Delete quote"
            className="text-zinc-500 hover:text-red-500 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </header>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <a
          href={`mailto:${q.email}`}
          className="flex items-center gap-2 text-zinc-300 hover:text-[#FF5A00]"
        >
          <Mail className="w-4 h-4 text-[#FF5A00]" /> {q.email}
        </a>
        <a
          href={`tel:${q.phone}`}
          className="flex items-center gap-2 text-zinc-300 hover:text-[#FF5A00]"
        >
          <Phone className="w-4 h-4 text-[#FF5A00]" /> {q.phone}
        </a>
        {q.company && (
          <span className="flex items-center gap-2 text-zinc-300">
            <Building2 className="w-4 h-4 text-[#FF5A00]" /> {q.company}
          </span>
        )}
        {q.location && (
          <span className="flex items-center gap-2 text-zinc-300">
            <MapPin className="w-4 h-4 text-[#FF5A00]" /> {q.location}
          </span>
        )}
      </div>
      <p className="mt-5 bg-zinc-950 border border-zinc-800 p-4 text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">
        {q.message}
      </p>
    </article>
  );
}

function ContactCard({ c, onDelete }) {
  return (
    <article
      data-testid={`admin-contact-${c.id}`}
      className="bg-zinc-900 border border-zinc-800 p-6"
    >
      <header className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-2xl tracking-wider uppercase text-zinc-50">
            {c.name}
          </h3>
          {c.subject && (
            <div className="font-heading text-[10px] tracking-[0.3em] uppercase text-[#FF5A00] mt-1">
              {c.subject}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs font-heading tracking-wider uppercase text-zinc-500">
            {new Date(c.created_at).toLocaleString()}
          </span>
          <button
            data-testid={`delete-contact-${c.id}`}
            onClick={onDelete}
            aria-label="Delete message"
            className="text-zinc-500 hover:text-red-500 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </header>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <a
          href={`mailto:${c.email}`}
          className="flex items-center gap-2 text-zinc-300 hover:text-[#FF5A00]"
        >
          <Mail className="w-4 h-4 text-[#FF5A00]" /> {c.email}
        </a>
        {c.phone && (
          <a
            href={`tel:${c.phone}`}
            className="flex items-center gap-2 text-zinc-300 hover:text-[#FF5A00]"
          >
            <Phone className="w-4 h-4 text-[#FF5A00]" /> {c.phone}
          </a>
        )}
      </div>
      <p className="mt-5 bg-zinc-950 border border-zinc-800 p-4 text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">
        {c.message}
      </p>
    </article>
  );
}

function Loading() {
  return (
    <div className="py-16 grid place-items-center">
      <Loader2 className="w-5 h-5 animate-spin text-[#FF5A00]" />
    </div>
  );
}

function Empty({ label }) {
  return (
    <div className="py-16 text-center text-zinc-500 font-heading tracking-widest uppercase text-sm">
      {label}
    </div>
  );
}
